import { useState, useEffect } from "react";
import { Users, Copy, Check, RefreshCw, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  fetchFriendCode, regenerateFriendCode, addFriendByCode, fetchFriends, removeFriendApi,
} from "../lib/api";

// Page "Amis" : code personnel à partager + liste des amis ajoutés, chacun
// menant à sa collection en lecture seule (cf. FriendCollectionView).
export const FriendsView = ({ onViewFriend }) => {
  const [friendCode, setFriendCode] = useState(null);
  const [friendCodeBusy, setFriendCodeBusy] = useState(false);
  const [friendCodeCopied, setFriendCodeCopied] = useState(false);
  const [friends, setFriends] = useState([]);
  const [addCodeInput, setAddCodeInput] = useState("");
  const [addFriendBusy, setAddFriendBusy] = useState(false);
  const [friendMsg, setFriendMsg] = useState(null);

  const reloadFriends = () => fetchFriends().then(setFriends).catch(() => {});

  useEffect(() => {
    fetchFriendCode().then(setFriendCode).catch(() => {});
    reloadFriends();
  }, []);

  const runRegenerateCode = async () => {
    setFriendCodeBusy(true);
    try { setFriendCode(await regenerateFriendCode()); }
    finally { setFriendCodeBusy(false); }
  };

  const runCopyCode = async () => {
    if (!friendCode) return;
    try {
      await navigator.clipboard.writeText(friendCode);
      setFriendCodeCopied(true);
      setTimeout(() => setFriendCodeCopied(false), 1500);
    } catch { /* presse-papier indisponible : rien à faire, le code reste affiché */ }
  };

  const runAddFriend = async () => {
    const code = addCodeInput.trim();
    if (!code) return;
    setAddFriendBusy(true); setFriendMsg(null);
    try {
      const { username } = await addFriendByCode(code);
      setAddCodeInput("");
      setFriendMsg({ text: `${username} a été ajouté à tes amis.` });
      reloadFriends();
    } catch (e) {
      setFriendMsg({ error: true, text: e?.response?.data?.detail || "Code invalide." });
    } finally { setAddFriendBusy(false); }
  };

  const runRemoveFriend = async (friendId) => {
    await removeFriendApi(friendId).catch(() => {});
    reloadFriends();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-1">Amis</h2>
      <p className="text-sm text-zinc-500 mb-6">
        Partage ton code avec un ami pour qu'il puisse consulter ta collection
        en lecture seule (il ne pourra jamais rien modifier). Ajoute le sien de
        la même façon pour voir la sienne.
      </p>

      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-zinc-500">Ton code :</span>
        <code className="text-sm font-mono text-white bg-zinc-800 border border-zinc-700 rounded px-2 py-1">
          {friendCode || "…"}
        </code>
        <button onClick={runCopyCode} title="Copier" className="text-zinc-500 hover:text-white">
          {friendCodeCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        </button>
        <Button onClick={runRegenerateCode} disabled={friendCodeBusy} variant="ghost"
          className="text-zinc-400 hover:text-white disabled:opacity-40 h-7 px-2 text-xs">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${friendCodeBusy ? "animate-spin" : ""}`} />
          Régénérer
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Input placeholder="Code d'un ami" value={addCodeInput}
          onChange={(e) => setAddCodeInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") runAddFriend(); }}
          className="max-w-[180px] h-9 bg-zinc-800 border-zinc-700 font-mono uppercase" />
        <Button onClick={runAddFriend} disabled={addFriendBusy || !addCodeInput.trim()}
          className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40">
          Ajouter
        </Button>
      </div>
      {friendMsg && (
        <p className={`text-sm mb-4 ${friendMsg.error ? "text-red-400" : "text-emerald-400"}`}>
          {friendMsg.text}
        </p>
      )}

      {friends.length > 0 ? (
        <div className="space-y-1 mt-4">
          {friends.map(f => (
            <div key={f.friend_id} className="flex items-center gap-2 bg-zinc-800/50 rounded px-3 py-2">
              <Users className="w-4 h-4 text-zinc-500" />
              <span className="flex-1 text-sm text-zinc-200">{f.username}</span>
              <Button onClick={() => onViewFriend?.(f)}
                className="h-7 px-2 text-xs bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
                Voir sa collection
              </Button>
              <button onClick={() => runRemoveFriend(f.friend_id)} title="Retirer"
                className="text-zinc-600 hover:text-red-400">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-zinc-500 mt-4">Aucun ami ajouté pour l'instant.</p>
      )}
    </div>
  );
};
